/**
 * Airtable React Hooks
 * 
 * Custom React hooks for working with Airtable data in components
 * Integrates with React Query for efficient data fetching
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchRecords,
  createRecord,
  updateRecord,
  deleteRecord,
  AirtableRecord,
  QueryOptions,
  TableRecordType,
  CROModule,
  StainingPlatformModule,
  ImageScanningModule,
  ImageAnalysisModule,
  CompanyModule,
  deserializeRecords,
  serialize,
  validateModel,
  dynamicDeserialize
} from './';
import { TableModelType } from './transform';
import { useState, useEffect, useRef } from "react";

// Query key factory
const airtableKeys = {
  all: ['airtable'] as const,
  table: (table: string) => [...airtableKeys.all, table] as const,
  records: (table: string, options?: QueryOptions) => 
    [...airtableKeys.table(table), 'records', options] as const,
  record: (table: string, id: string) => 
    [...airtableKeys.table(table), 'record', id] as const,
};

/**
 * Hook to fetch records from an Airtable table by tableId
 * @param tableId Airtable table ID (required)
 */
export function useAirtableRecords(
  tableId: string,
  options?: QueryOptions,
  queryOptions?: {
    enabled?: boolean;
    refetchInterval?: number | false;
    refetchOnWindowFocus?: boolean;
    staleTime?: number;
    transform?: boolean;
  }
) {
  const { transform = true, ...restQueryOptions } = queryOptions || {};
  return useQuery({
    queryKey: airtableKeys.records(tableId, options),
    queryFn: async () => {
      const response = await fetchRecords(tableId, options);
      if (transform) {
        return deserializeRecords(tableId, response.records) as TableModelType<string>[];
      }
      return response.records as TableRecordType<string>[];
    },
    enabled: queryOptions?.enabled ?? true,
    ...restQueryOptions,
  });
}

/**
 * Hook to fetch a single record from an Airtable table by tableId
 * @param tableId Airtable table ID (required)
 */
export function useAirtableRecord(
  tableId: string,
  recordId: string,
  queryOptions?: {
    enabled?: boolean;
    refetchInterval?: number | false;
    refetchOnWindowFocus?: boolean;
    staleTime?: number;
    transform?: boolean;
  }
) {
  const { transform = true, ...restQueryOptions } = queryOptions || {};
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: airtableKeys.record(tableId, recordId),
    queryFn: async () => {
      // Check if we can get the record from cache first
      const cachedRecords = queryClient.getQueryData<TableRecordType<string>[]>(
        airtableKeys.records(tableId)
      );
      if (cachedRecords) {
        const cachedRecord = cachedRecords.find(record => record.id === recordId);
        if (cachedRecord) {
          if (transform) {
            return deserializeRecords(tableId, [cachedRecord])[0] as TableModelType<string>;
          }
          return cachedRecord;
        }
      }
      // Not in cache, fetch directly
      const response = await fetchRecords(tableId, {
        filterByFormula: `RECORD_ID() = '${recordId}'`,
        maxRecords: 1
      });
      if (response.records.length === 0) {
        throw new Error(`Record not found with ID: ${recordId}`);
      }
      if (transform) {
        return deserializeRecords(tableId, [response.records[0]])[0] as TableModelType<string>;
      }
      return response.records[0] as TableRecordType<string>;
    },
    enabled: queryOptions?.enabled ?? true,
    ...restQueryOptions,
  });
}

/**
 * Mutation hooks (create, update, delete) now require the resolved table name as a parameter.
 * The caller must resolve the table name using getTableNameByKey before calling these hooks.
 */
export function useCreateRecord(resolvedTableName: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<TableModelType<string>>) => {
      const validatedData = validateModel(resolvedTableName, data);
      const fields = serialize(resolvedTableName, validatedData);
      const record = await createRecord(resolvedTableName, fields);
      return deserializeRecords(resolvedTableName, [record])[0] as TableModelType<string>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: airtableKeys.table(resolvedTableName)
      });
    }
  });
}

export function useUpdateRecord(resolvedTableName: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<TableModelType<string>> }) => {
      const validatedData = validateModel(resolvedTableName, data);
      const fields = serialize(resolvedTableName, validatedData);
      const record = await updateRecord(resolvedTableName, id, fields);
      return deserializeRecords(resolvedTableName, [record])[0] as TableModelType<string>;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: airtableKeys.table(resolvedTableName)
      });
      queryClient.invalidateQueries({
        queryKey: airtableKeys.record(resolvedTableName, variables.id)
      });
    }
  });
}

export function useDeleteRecord(resolvedTableName: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      return deleteRecord(resolvedTableName, id);
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({
        queryKey: airtableKeys.table(resolvedTableName)
      });
      queryClient.invalidateQueries({
        queryKey: airtableKeys.record(resolvedTableName, id)
      });
      queryClient.setQueryData<TableRecordType<string>[]>(
        airtableKeys.records(resolvedTableName),
        (oldData) => oldData?.filter(record => record.id !== id) || []
      );
    }
  });
}

/**
 * Hook to fetch records from an Airtable table using dynamic schema by tableId
 * @param tableId Airtable table ID (required)
 */
export function useDynamicAirtableRecords(
  tableId: string,
  options?: QueryOptions,
  queryOptions?: {
    enabled?: boolean;
    refetchInterval?: number | false;
    refetchOnWindowFocus?: boolean;
    staleTime?: number;
  }
) {
  return useQuery({
    queryKey: ['dynamicAirtableRecords', tableId, options],
    queryFn: async () => {
      if (!tableId) return [];
      // Fetch from our own API route, which handles schema and transformation server-side
      const url = `/api/airtable?table=${encodeURIComponent(tableId)}`;
      const res = await fetch(url);
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch Airtable data');
      }
      const data = await res.json();
      return data.records || [];
    },
    enabled: queryOptions?.enabled ?? true,
    ...queryOptions,
  });
}

// ============================================================================
// Table-specific React Query Hooks
// ============================================================================

/**
 * CRO Table Hooks
 */
export const useCROs = {
  /**
   * Get all CRO records
   */
  getAll: (tableId: string, options?: QueryOptions, queryOptions?: any) => {
    return useQuery({
      queryKey: airtableKeys.records(tableId, options),
      queryFn: () => CROModule.getAll(tableId, options),
      enabled: queryOptions?.enabled ?? true,
      ...queryOptions,
    });
  },
  
  /**
   * Get a single CRO by ID
   */
  getById: (tableId: string, id: string, queryOptions?: any) => {
    return useQuery({
      queryKey: airtableKeys.record(tableId, id),
      queryFn: () => CROModule.getById(tableId, id),
      enabled: queryOptions?.enabled ?? true,
      ...queryOptions,
    });
  },
  
  /**
   * Get CROs by name (partial match)
   */
  getByName: (tableId: string, name: string, options?: Omit<QueryOptions, 'filterByFormula'>, queryOptions?: any) => {
    return useQuery({
      queryKey: airtableKeys.table(tableId),
      queryFn: () => CROModule.getByName(tableId, name, options),
      enabled: queryOptions?.enabled ?? true,
      ...queryOptions,
    });
  },
  
  /**
   * Get CROs by staining platform ID
   */
  getByStainingPlatform: (tableId: string, platformId: string, queryOptions?: any) => {
    return useQuery({
      queryKey: airtableKeys.table(tableId),
      queryFn: () => CROModule.getByStainingPlatform(tableId, platformId),
      enabled: queryOptions?.enabled ?? true,
      ...queryOptions,
    });
  },
  
  /**
   * Get CROs by image scanning method ID
   */
  getByImageScanning: (tableId: string, scanningId: string, queryOptions?: any) => {
    return useQuery({
      queryKey: airtableKeys.table(tableId),
      queryFn: () => CROModule.getByImageScanning(tableId, scanningId),
      enabled: queryOptions?.enabled ?? true,
      ...queryOptions,
    });
  },
  
  /**
   * Get CROs by image analysis method ID
   */
  getByImageAnalysis: (tableId: string, analysisId: string, queryOptions?: any) => {
    return useQuery({
      queryKey: airtableKeys.table(tableId),
      queryFn: () => CROModule.getByImageAnalysis(tableId, analysisId),
      enabled: queryOptions?.enabled ?? true,
      ...queryOptions,
    });
  },
  
  /**
   * Create a new CRO record
   */
  create: (tableId: string) => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: (fields: Omit<TableRecordType<string>["fields"], "id">) => CROModule.create(tableId, fields),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: airtableKeys.table(tableId),
        });
      },
    });
  },
  
  /**
   * Update an existing CRO record
   */
  update: (tableId: string) => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: ({ id, fields }: { id: string; fields: Partial<TableRecordType<string>["fields"]> }) => CROModule.update(tableId, id, fields),
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries({
          queryKey: airtableKeys.table(tableId),
        });
        queryClient.invalidateQueries({
          queryKey: airtableKeys.record(tableId, variables.id),
        });
      },
    });
  },
  
  /**
   * Delete a CRO record
   */
  delete: (tableId: string) => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: (id: string) => CROModule.delete(tableId, id),
      onSuccess: (_, id) => {
        queryClient.invalidateQueries({
          queryKey: airtableKeys.table(tableId),
        });
        queryClient.removeQueries({
          queryKey: airtableKeys.record(tableId, id),
        });
      },
    });
  },
};

/**
 * Staining Platform Table Hooks
 */
export const useStainingPlatforms = {
  /**
   * Get all staining platform records
   */
  getAll: (tableId: string, options?: QueryOptions, queryOptions?: any) => {
    return useQuery({
      queryKey: airtableKeys.records(tableId, options),
      queryFn: () => StainingPlatformModule.getAll(tableId, options),
      enabled: queryOptions?.enabled ?? true,
      ...queryOptions,
    });
  },
  
  /**
   * Get a single staining platform by ID
   */
  getById: (tableId: string, id: string, queryOptions?: any) => {
    return useQuery({
      queryKey: airtableKeys.record(tableId, id),
      queryFn: () => StainingPlatformModule.getById(tableId, id),
      enabled: queryOptions?.enabled ?? true,
      ...queryOptions,
    });
  },
  
  /**
   * Get staining platforms by manufacturer
   */
  getByManufacturer: (tableId: string, manufacturer: string, queryOptions?: any) => {
    return useQuery({
      queryKey: airtableKeys.table(tableId),
      queryFn: () => StainingPlatformModule.getByManufacturer(tableId, manufacturer),
      enabled: queryOptions?.enabled ?? true,
      ...queryOptions,
    });
  },
  
  /**
   * Get staining platforms by company ID
   */
  getByCompany: (tableId: string, companyId: string, queryOptions?: any) => {
    return useQuery({
      queryKey: airtableKeys.table(tableId),
      queryFn: () => StainingPlatformModule.getByCompany(tableId, companyId),
      enabled: queryOptions?.enabled ?? true,
      ...queryOptions,
    });
  },
  
  /**
   * Get staining platforms by sample type
   */
  getBySampleType: (tableId: string, sampleType: string, queryOptions?: any) => {
    return useQuery({
      queryKey: airtableKeys.table(tableId),
      queryFn: () => StainingPlatformModule.getBySampleType(tableId, sampleType),
      enabled: queryOptions?.enabled ?? true,
      ...queryOptions,
    });
  },

  // Mutation hooks for CRUD operations
  create: (tableId: string) => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: (fields: Omit<TableRecordType<string>["fields"], "id">) => StainingPlatformModule.create(tableId, fields),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: airtableKeys.table(tableId),
        });
      },
    });
  },
  
  update: (tableId: string) => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: ({ id, fields }: { id: string; fields: Partial<TableRecordType<string>["fields"]> }) => StainingPlatformModule.update(tableId, id, fields),
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries({
          queryKey: airtableKeys.table(tableId),
        });
        queryClient.invalidateQueries({
          queryKey: airtableKeys.record(tableId, variables.id),
        });
      },
    });
  },
  
  delete: (tableId: string) => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: (id: string) => StainingPlatformModule.delete(tableId, id),
      onSuccess: (_, id) => {
        queryClient.invalidateQueries({
          queryKey: airtableKeys.table(tableId),
        });
        queryClient.removeQueries({
          queryKey: airtableKeys.record(tableId, id),
        });
      },
    });
  },
};

/**
 * Image Scanning Table Hooks
 */
export const useImageScanning = {
  // Query hooks
  getAll: (tableId: string, options?: QueryOptions, queryOptions?: any) => {
    return useQuery({
      queryKey: airtableKeys.records(tableId, options),
      queryFn: () => ImageScanningModule.getAll(tableId, options),
      enabled: queryOptions?.enabled ?? true,
      ...queryOptions,
    });
  },
  
  getById: (tableId: string, id: string, queryOptions?: any) => {
    return useQuery({
      queryKey: airtableKeys.record(tableId, id),
      queryFn: () => ImageScanningModule.getById(tableId, id),
      enabled: queryOptions?.enabled ?? true,
      ...queryOptions,
    });
  },
  
  getByTechnology: (tableId: string, technology: string, queryOptions?: any) => {
    return useQuery({
      queryKey: airtableKeys.table(tableId),
      queryFn: () => ImageScanningModule.getByTechnology(tableId, technology),
      enabled: queryOptions?.enabled ?? true,
      ...queryOptions,
    });
  },
  
  getByCompany: (tableId: string, companyId: string, queryOptions?: any) => {
    return useQuery({
      queryKey: airtableKeys.table(tableId),
      queryFn: () => ImageScanningModule.getByCompany(tableId, companyId),
      enabled: queryOptions?.enabled ?? true,
      ...queryOptions,
    });
  },
  
  getBySupportedSlide: (tableId: string, slideType: string, queryOptions?: any) => {
    return useQuery({
      queryKey: airtableKeys.table(tableId),
      queryFn: () => ImageScanningModule.getBySupportedSlide(tableId, slideType),
      enabled: queryOptions?.enabled ?? true,
      ...queryOptions,
    });
  },

  // Mutation hooks
  create: (tableId: string) => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: (fields: Omit<TableRecordType<string>["fields"], "id">) => ImageScanningModule.create(tableId, fields),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: airtableKeys.table(tableId),
        });
      },
    });
  },
  
  update: (tableId: string) => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: ({ id, fields }: { id: string; fields: Partial<TableRecordType<string>["fields"]> }) => ImageScanningModule.update(tableId, id, fields),
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries({
          queryKey: airtableKeys.table(tableId),
        });
        queryClient.invalidateQueries({
          queryKey: airtableKeys.record(tableId, variables.id),
        });
      },
    });
  },
  
  delete: (tableId: string) => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: (id: string) => ImageScanningModule.delete(tableId, id),
      onSuccess: (_, id) => {
        queryClient.invalidateQueries({
          queryKey: airtableKeys.table(tableId),
        });
        queryClient.removeQueries({
          queryKey: airtableKeys.record(tableId, id),
        });
      },
    });
  },
};

/**
 * Image Analysis Table Hooks
 */
export const useImageAnalysis = {
  // Query hooks
  getAll: (tableId: string, options?: QueryOptions, queryOptions?: any) => {
    return useQuery({
      queryKey: airtableKeys.records(tableId, options),
      queryFn: () => ImageAnalysisModule.getAll(tableId, options),
      enabled: queryOptions?.enabled ?? true,
      ...queryOptions,
    });
  },
  
  getById: (tableId: string, id: string, queryOptions?: any) => {
    return useQuery({
      queryKey: airtableKeys.record(tableId, id),
      queryFn: () => ImageAnalysisModule.getById(tableId, id),
      enabled: queryOptions?.enabled ?? true,
      ...queryOptions,
    });
  },
  
  getByAnalysisType: (tableId: string, analysisType: string, queryOptions?: any) => {
    return useQuery({
      queryKey: airtableKeys.table(tableId),
      queryFn: () => ImageAnalysisModule.getByAnalysisType(tableId, analysisType),
      enabled: queryOptions?.enabled ?? true,
      ...queryOptions,
    });
  },
  
  getByCompany: (tableId: string, companyId: string, queryOptions?: any) => {
    return useQuery({
      queryKey: airtableKeys.table(tableId),
      queryFn: () => ImageAnalysisModule.getByCompany(tableId, companyId),
      enabled: queryOptions?.enabled ?? true,
      ...queryOptions,
    });
  },
  
  getBySupportedFormat: (tableId: string, format: string, queryOptions?: any) => {
    return useQuery({
      queryKey: airtableKeys.table(tableId),
      queryFn: () => ImageAnalysisModule.getBySupportedFormat(tableId, format),
      enabled: queryOptions?.enabled ?? true,
      ...queryOptions,
    });
  },
  
  getCloudBased: (tableId: string, queryOptions?: any) => {
    return useQuery({
      queryKey: airtableKeys.table(tableId),
      queryFn: () => ImageAnalysisModule.getCloudBased(tableId),
      enabled: queryOptions?.enabled ?? true,
      ...queryOptions,
    });
  },
  
  getMachineLearningCapable: (tableId: string, queryOptions?: any) => {
    return useQuery({
      queryKey: airtableKeys.table(tableId),
      queryFn: () => ImageAnalysisModule.getMachineLearningCapable(tableId),
      enabled: queryOptions?.enabled ?? true,
      ...queryOptions,
    });
  },

  // Mutation hooks
  create: (tableId: string) => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: (fields: Omit<TableRecordType<string>["fields"], "id">) => ImageAnalysisModule.create(tableId, fields),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: airtableKeys.table(tableId),
        });
      },
    });
  },
  
  update: (tableId: string) => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: ({ id, fields }: { id: string; fields: Partial<TableRecordType<string>["fields"]> }) => ImageAnalysisModule.update(tableId, id, fields),
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries({
          queryKey: airtableKeys.table(tableId),
        });
        queryClient.invalidateQueries({
          queryKey: airtableKeys.record(tableId, variables.id),
        });
      },
    });
  },
  
  delete: (tableId: string) => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: (id: string) => ImageAnalysisModule.delete(tableId, id),
      onSuccess: (_, id) => {
        queryClient.invalidateQueries({
          queryKey: airtableKeys.table(tableId),
        });
        queryClient.removeQueries({
          queryKey: airtableKeys.record(tableId, id),
        });
      },
    });
  },
};

/**
 * Company Table Hooks
 */
export const useCompanies = {
  // Query hooks
  getAll: (tableId: string, options?: QueryOptions, queryOptions?: any) => {
    return useQuery({
      queryKey: airtableKeys.records(tableId, options),
      queryFn: () => CompanyModule.getAll(tableId, options),
      enabled: queryOptions?.enabled ?? true,
      ...queryOptions,
    });
  },
  
  getById: (tableId: string, id: string, queryOptions?: any) => {
    return useQuery({
      queryKey: airtableKeys.record(tableId, id),
      queryFn: () => CompanyModule.getById(tableId, id),
      enabled: queryOptions?.enabled ?? true,
      ...queryOptions,
    });
  },
  
  getByCategory: (tableId: string, category: string, queryOptions?: any) => {
    return useQuery({
      queryKey: airtableKeys.table(tableId),
      queryFn: () => CompanyModule.getByCategory(tableId, category),
      enabled: queryOptions?.enabled ?? true,
      ...queryOptions,
    });
  },
  
  getByStainingPlatform: (tableId: string, platformId: string, queryOptions?: any) => {
    return useQuery({
      queryKey: airtableKeys.table(tableId),
      queryFn: () => CompanyModule.getByStainingPlatform(tableId, platformId),
      enabled: queryOptions?.enabled ?? true,
      ...queryOptions,
    });
  },
  
  getByImageScanning: (tableId: string, scanningId: string, queryOptions?: any) => {
    return useQuery({
      queryKey: airtableKeys.table(tableId),
      queryFn: () => CompanyModule.getByImageScanning(tableId, scanningId),
      enabled: queryOptions?.enabled ?? true,
      ...queryOptions,
    });
  },
  
  getByImageAnalysis: (tableId: string, analysisId: string, queryOptions?: any) => {
    return useQuery({
      queryKey: airtableKeys.table(tableId),
      queryFn: () => CompanyModule.getByImageAnalysis(tableId, analysisId),
      enabled: queryOptions?.enabled ?? true,
      ...queryOptions,
    });
  },
  
  getByLocation: (tableId: string, location: string, queryOptions?: any) => {
    return useQuery({
      queryKey: airtableKeys.table(tableId),
      queryFn: () => CompanyModule.getByLocation(tableId, location),
      enabled: queryOptions?.enabled ?? true,
      ...queryOptions,
    });
  },

  // Mutation hooks
  create: (tableId: string) => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: (fields: Omit<TableRecordType<string>["fields"], "id">) => CompanyModule.create(tableId, fields),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: airtableKeys.table(tableId),
        });
      },
    });
  },
  
  update: (tableId: string) => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: ({ id, fields }: { id: string; fields: Partial<TableRecordType<string>["fields"]> }) => CompanyModule.update(tableId, id, fields),
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries({
          queryKey: airtableKeys.table(tableId),
        });
        queryClient.invalidateQueries({
          queryKey: airtableKeys.record(tableId, variables.id),
        });
      },
    });
  },
  
  delete: (tableId: string) => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: (id: string) => CompanyModule.delete(tableId, id),
      onSuccess: (_, id) => {
        queryClient.invalidateQueries({
          queryKey: airtableKeys.table(tableId),
        });
        queryClient.removeQueries({
          queryKey: airtableKeys.record(tableId, id),
        });
      },
    });
  },
};

export function useBatchAirtableLookup(table: string, ids: string[]) {
  const [lookup, setLookup] = useState<Record<string, any>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const cacheRef = useRef<{ [key: string]: any }>({});

  useEffect(() => {
    if (!table || !ids || ids.length === 0) return;
    const missingIds = ids.filter(id => !(id in cacheRef.current));
    if (missingIds.length === 0) {
      // All IDs are cached
      setLookup(ids.reduce((acc: { [key: string]: any }, id) => { acc[id] = cacheRef.current[id]; return acc; }, {}));
      return;
    }
    setIsLoading(true);
    setIsError(false);
    // Build filterByFormula for batch fetch
    const filter = `OR(${missingIds.map(id => `RECORD_ID()='${id}'`).join(",")})`;
    fetch(`/api/airtable?table=${table}&filterByFormula=${encodeURIComponent(filter)}`)
      .then(res => res.json())
      .then(data => {
        const records = Array.isArray(data) ? data : data.records;
        if (!Array.isArray(records)) throw new Error("No records returned");
        const newMap: { [key: string]: any } = {};
        for (const rec of records) {
          newMap[rec.id] = rec;
          cacheRef.current[rec.id] = rec;
        }
        // Merge with existing cache
        const merged = { ...cacheRef.current };
        setLookup(ids.reduce((acc: { [key: string]: any }, id) => { acc[id] = merged[id]; return acc; }, {}));
        setIsLoading(false);
      })
      .catch(() => {
        setIsError(true);
        setIsLoading(false);
      });
    // eslint-disable-next-line
  }, [table, JSON.stringify(ids)]);

  return { lookup, isLoading, isError };
} 
